import { useEffect, useRef, useState } from 'react';
import { Card, Collapse, Group, Stack, Text } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconShieldLock } from '@tabler/icons-react';
import type { ReactNode } from 'react';

/**
 * TIER 2 disclosure: the expert fallback, collapsed by default and visually
 * subordinate to the primary path. Extracted from the pattern already used for
 * the manual-credentials card on the Meta page so every "you probably don't
 * need this" surface looks and behaves the same.
 *
 * `defaultOpen` is *live*, not just an initial value: it may start `false` and
 * flip `true` once a query resolves and reveals something in here the user has
 * to fix (a missing app secret, an unsubscribed webhook). When it does, the
 * section opens itself — a broken connection must never hide its own remedy
 * behind a collapsed row. Once the user has toggled it by hand, their choice
 * wins and we stop auto-opening.
 */
export interface AdvancedSectionProps {
  /** Defaults to "Advanced setup". */
  title?: string;
  /** One line naming who actually needs what's inside. */
  description?: string;
  icon?: ReactNode;
  /** Open on mount, and auto-open if it later becomes true. */
  defaultOpen?: boolean;
  /** Rendered next to the title — e.g. a "Needs attention" badge. */
  badge?: ReactNode;
  children: ReactNode;
}

export function AdvancedSection({
  title = 'Advanced setup',
  description,
  icon,
  defaultOpen = false,
  badge,
  children,
}: AdvancedSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const userToggled = useRef(false);
  const prevDefault = useRef(defaultOpen);

  useEffect(() => {
    // Only react to a false -> true transition, and only while the user hasn't
    // expressed a preference of their own.
    if (defaultOpen && !prevDefault.current && !userToggled.current) setOpen(true);
    prevDefault.current = defaultOpen;
  }, [defaultOpen]);

  const toggle = () => {
    userToggled.current = true;
    setOpen((v) => !v);
  };

  return (
    <Card>
      <Stack gap="xs">
        <Group justify="space-between" style={{ cursor: 'pointer' }} onClick={toggle}>
          <Group gap="xs">
            {icon ?? <IconShieldLock size={16} />}
            <Stack gap={0}>
              <Group gap="xs">
                <Text fw={600}>{title}</Text>
                {badge}
              </Group>
              {description && (
                <Text size="xs" c="dimmed">
                  {description}
                </Text>
              )}
            </Stack>
          </Group>
          {open ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
        </Group>
        <Collapse expanded={open}>
          <Stack gap="md" mt="xs">
            {children}
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
}
